import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';

/**
 * Determines whether to show the object editor (ExpressionObjectPlaceholder) or regular expression chip.
 * 
 * Centralized logic used across ExpressionPlaceholder, ExpressionObjectNestedPlaceholder, 
 * and ExpressionPlaceholderObjectInline to ensure consistent behavior.
 * 
 * @param expression - The expression to evaluate (can be undefined)
 * @param expressionType - The Type property of the expression (can be undefined)
 * @param isObjectType - Whether the parameter/property expects an object type
 * @returns true if the object editor should be shown, false if a chip should be shown
 */
export function shouldShowObjectEditor(
    expression: ExpressionModel | undefined,
    expressionType: string | undefined,
    isObjectType: boolean
): boolean {
    // Show object editor when:
    // 1. No expression exists AND parameter expects an object → allows creating object inline
    // 2. Expression Type is explicitly "Object" → shows object property editor
    // 3. Do NOT show for ObjectMap or CollectionMap even if they expect objects → avoid recursion

    if (!expression && isObjectType) {
        return true;
    }

    if (expressionType === ExpressionTypes.Object) {
        return true;
    }

    // For all other cases (OperationCall, Var, Const, ObjectMap, CollectionMap, etc.), show chip
    return false;
}

/**
 * Determines whether to show the collection editor (ExpressionCollectionInline) or regular expression chip.
 * 
 * @param expression - The expression to evaluate (can be undefined)
 * @param expressionType - The Type property of the expression (can be undefined)
 * @param isCollectionType - Whether the parameter expects a collection type
 * @returns true if the collection editor should be shown, false if a chip should be shown
 */
export function shouldShowCollectionEditor(
    expression: ExpressionModel | undefined,
    expressionType: string | undefined,
    isCollectionType: boolean
): boolean {
    // Show collection editor when:
    // 1. No expression exists AND parameter expects a collection → allows creating collection inline
    // 2. Expression Type is explicitly "Collection" → shows collection items editor
    // 3. Do NOT show for CollectionMap → avoid confusion with map operations

    if (!expression && isCollectionType) {
        return true;
    }

    if (expressionType === ExpressionTypes.Collection) {
        return true;
    }

    return false;
}
